/**
 * Shown when a SUPER_ADMIN has paused invoicing for the organization. The pause models
 * the payment provider being temporarily unavailable, so it blocks the operations that
 * reach the provider (send / resend / void) while leaving browsing and drafting usable.
 */
export const INVOICING_PAUSED_MESSAGE =
  'Invoicing is paused right now — our payment provider is undergoing maintenance. Please try again soon.';

export enum InvoiceStatus {
  DRAFT = 'DRAFT',
  SENDING = 'SENDING',
  SENT = 'SENT',
  PAID = 'PAID',
  VOIDED = 'VOIDED',
  SEND_FAILED = 'SEND_FAILED',
}

export enum InvoiceLineItemSourceType {
  TASK_MATERIAL = 'TASK_MATERIAL',
  TASK_EQUIPMENT = 'TASK_EQUIPMENT',
  LINE_ITEM = 'LINE_ITEM',
  DELEGATED_TASK_COST = 'DELEGATED_TASK_COST',
}

export interface InvoiceLineItem {
  invoiceLineItemId: string;
  description: string;
  quantity: number;
  unitOfMeasure: string;
  unitPrice: number;
  lineTotal: number;
  taxAmount: number;
  discountAmount: number;
  displayOrder: number;
  sourceType?: InvoiceLineItemSourceType;
  sourceEntityId?: string;
  taskId?: string | null;
  taskName?: string | null;
}

export interface InvoiceTaskAssociation {
  invoiceTaskAssociationId: string;
  taskId: string;
}

export interface InvoiceStatusHistoryEntry {
  invoiceStatusHistoryId: string;
  fromStatus?: InvoiceStatus;
  toStatus: InvoiceStatus;
  reason?: string;
  actorUserId?: string;
  createdAt: string;
}

export interface Invoice {
  invoiceId: string;
  invoiceNumber?: string;
  status: InvoiceStatus;
  recipientEmail: string;
  subtotal: number;
  taxRate: number;
  tax: number;
  discountRate: number;
  discountFixedAmount: number;
  discountAmount: number;
  total: number;
  currency: string;
  paymentTerms?: number;
  orderDescription?: string;
  poNumber?: string;
  nmiInvoiceId?: string;
  nmiTransactionId?: string;
  sentAt?: string;
  paidAt?: string;
  voidedAt?: string;
  lastError?: string;
  projectId: string;
  clientId?: string;
  organizationId: string;
  createdById?: string;
  lineItems: InvoiceLineItem[];
  taskAssociations: InvoiceTaskAssociation[];
  statusHistory?: InvoiceStatusHistoryEntry[];
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface AvailableBudgetItem {
  sourceType: InvoiceLineItemSourceType;
  sourceEntityId: string;
  description: string;
  quantity: number;
  unitOfMeasure: string;
  unitPrice: number;
  total: number;
  chargeToClient: boolean;
  isAvailableForInvoice: boolean;
  chargedToInvoiceId?: string;
  taskId?: string;
  taskName?: string;
  /** Per-line discount applied to the unit price. Only populated for LINE_ITEM rows. */
  discountType?: 'USD' | 'PERCENT' | null;
  discountValue?: number;
  discountAmount?: number;
}

export interface AvailableBudgetItemsResult {
  materials: AvailableBudgetItem[];
  equipment: AvailableBudgetItem[];
  expenses: AvailableBudgetItem[];
  delegatedTaskCosts: AvailableBudgetItem[];
}

// Sprint 29, story-148: removed the hardcoded-English `INVOICE_STATUS_LABELS` map that used to
// live here — its one consumer (`InvoiceStatusBadge`) now builds its label map from
// `useTranslation('enumLabels')` directly, so a locale-blind constant no longer exists.

export const INVOICE_EDITABLE_STATUSES: ReadonlySet<InvoiceStatus> = new Set([
  InvoiceStatus.DRAFT,
  InvoiceStatus.SEND_FAILED,
]);

export const isInvoiceEditable = (status: InvoiceStatus): boolean => INVOICE_EDITABLE_STATUSES.has(status);

export interface InvoiceFilterInput {
  projectIds?: string[];
  taskIds?: string[];
  statuses?: InvoiceStatus[];
  clientId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export interface PaginatedInvoicesResult {
  invoices: Invoice[];
  count: number;
}
